/**
 * OmegaPipelinePage — /omega/pipeline
 * Sprint 42 E4 / QC-004: real-time autonomous-factory pipeline monitor.
 * One row per active pipeline run: completed stages, pending stages, and any
 * stages parked at a founder-approval gate (Spec→Test, Test→Deploy, Deploy→Launch).
 * Reads the orchestrator run-state via GET /admin/pipeline/runs (W3.1 backend).
 */
import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';
import { signHashWithFounderKey } from '@/api/real/founderSign';

interface RunProgress {
  run_id: string;
  completed: string[];
  pending: string[];
  // Gate states — present once list_pipeline_runs surfaces them; defaulted defensively.
  awaiting_approval?: string[];
  rejected?: string[];
}

interface RunsResponse {
  runs: RunProgress[];
}

const STAGE_ORDER = ['discovery', 'spec', 'test', 'deploy', 'launch', 'monitor', 'cost'];

function Chips({ stages, color }: { stages: string[]; color: string }) {
  if (stages.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <span>
      {[...stages]
        .sort((a, b) => STAGE_ORDER.indexOf(a) - STAGE_ORDER.indexOf(b))
        .map((s) => (
          <span
            key={s}
            style={{
              display: 'inline-block',
              margin: '1px 3px 1px 0',
              padding: '1px 7px',
              borderRadius: 4,
              background: color,
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {s}
          </span>
        ))}
    </span>
  );
}

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** One parked stage + an Approve button. POSTs the founder gate decision. */
function ApproveGate({
  runId,
  stage,
  onDone,
}: {
  runId: string;
  stage: string;
  onDone: () => void;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState<string | null>(null);

  async function approve() {
    setState('busy');
    setMsg(null);
    try {
      // require_founder Path A verifies X-Founder-Signature over the FIXED access
      // challenge (SHA-256("founder-omega-access-v1")), not the request body.
      const sig = await signHashWithFounderKey(await sha256hex('founder-omega-access-v1'));
      await apiFetch<void>(
        `/admin/pipeline/run/${encodeURIComponent(runId)}/gate/${encodeURIComponent(stage)}/approve`,
        { method: 'POST', headers: { 'X-Founder-Signature': sig } },
      );
      setState('done');
      onDone();
    } catch (e) {
      setState('error');
      setMsg(e instanceof Error ? e.message : 'Approve failed');
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '2px 8px 2px 0' }}>
      <span
        style={{
          display: 'inline-block',
          padding: '1px 7px',
          borderRadius: 4,
          background: '#d97706',
          color: '#fff',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {stage}
      </span>
      <button
        type="button"
        onClick={approve}
        disabled={state === 'busy' || state === 'done'}
        style={{
          fontSize: 12,
          padding: '2px 10px',
          borderRadius: 4,
          border: 'none',
          cursor: state === 'busy' || state === 'done' ? 'default' : 'pointer',
          background: state === 'done' ? '#16a34a' : '#2563eb',
          color: '#fff',
          opacity: state === 'busy' ? 0.6 : 1,
        }}
      >
        {state === 'busy' ? 'Approving…' : state === 'done' ? '✓ Approved' : 'Approve'}
      </button>
      {state === 'error' && (
        <span style={{ color: '#dc2626', fontSize: 11 }} title={msg ?? ''}>
          {msg}
        </span>
      )}
    </span>
  );
}

export function OmegaPipelinePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery<RunsResponse>({
    queryKey: ['pipeline-runs'],
    queryFn: () => apiFetch<RunsResponse>('/admin/pipeline/runs'),
    refetchInterval: 10_000,
  });

  const runs = data?.runs ?? [];

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 8 }}>
        <Link to="/omega" style={{ color: '#2563eb', fontSize: 14 }}>← OMEGA</Link>
      </div>
      <h1 style={{ fontSize: 24, marginBottom: 4 }}>Pipeline Monitor</h1>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Every active product run through the autonomous factory. Stages parked at a
        founder-approval gate need your decision (Spec→Test, Test→Deploy, Deploy→Launch).
        Polls every 10s.
      </p>

      {isLoading && <p>Loading…</p>}
      {error && <p style={{ color: '#dc2626' }}>Failed to load pipeline runs.</p>}

      {!isLoading && runs.length === 0 && (
        <p style={{ color: '#6b7280' }}>No active pipeline runs.</p>
      )}

      {runs.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '8px 6px' }}>Run</th>
              <th style={{ padding: '8px 6px' }}>Completed</th>
              <th style={{ padding: '8px 6px' }}>Pending</th>
              <th style={{ padding: '8px 6px' }}>Awaiting approval</th>
              <th style={{ padding: '8px 6px' }}>Rejected</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.run_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>
                  {r.run_id.slice(0, 8)}
                </td>
                <td style={{ padding: '8px 6px' }}><Chips stages={r.completed} color="#16a34a" /></td>
                <td style={{ padding: '8px 6px' }}><Chips stages={r.pending} color="#2563eb" /></td>
                <td style={{ padding: '8px 6px' }}>
                  {(r.awaiting_approval ?? []).length === 0 ? (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  ) : (
                    (r.awaiting_approval ?? []).map((stage) => (
                      <ApproveGate
                        key={stage}
                        runId={r.run_id}
                        stage={stage}
                        onDone={() => void qc.invalidateQueries({ queryKey: ['pipeline-runs'] })}
                      />
                    ))
                  )}
                </td>
                <td style={{ padding: '8px 6px' }}>
                  <Chips stages={r.rejected ?? []} color="#dc2626" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
